import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Bell, Mic, Star, GraduationCap } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* Teacher notifications bell (top bar).
   Replaces the old "From your teachers" card that used to sit on the home
   page — same three sources (voice notes, grading feedback, newly verified
   teachers), same merge/sort, now surfaced as an unread count instead of a
   full-width card.

   "Unread" has no backend model, so it's derived client-side: anything
   newer than the timestamp last stored when the panel was opened counts as
   unread (localStorage, per browser). That keeps the badge meaningful
   without inventing a read-receipt API. */

const SEEN_KEY = "teacher-notifs-seen-at";

/* The bell is mounted twice (the mobile top bar and the desktop one, each
   hidden at the other's breakpoint), so the fetch is shared module-wide —
   same pattern as MadaniPage's surah-name cache — to avoid firing all three
   requests twice on every page load. */
let _itemsPromise = null;

function fetchTeacherItems() {
  if (_itemsPromise) return _itemsPromise;
  _itemsPromise = Promise.all([
    api.get("/voice-notes/mine").then((r) => r.data || []).catch(() => []),
    api.get("/feedback/me").then((r) => r.data || []).catch(() => []),
    api.get("/teachers").then((r) => (r.data?.teachers || []).slice(0, 3)).catch(() => []),
  ]).then(([notes, fbs, newTeachers]) => [
    ...notes.map((n) => ({
      kind: "voice", date: n.created_at,
      titleKey: (n.from_name || n.teacher_name) ? "teacherNotificationBell.voiceNoteFrom" : "teacherNotificationBell.voiceNoteFromTeacher", titleVars: { name: n.from_name || n.teacher_name },
      detail: n.note || "", detailKey: n.note ? "" : "teacherNotificationBell.tapToListen", to: "/app/voice-notes", icon: Mic,
    })),
    ...fbs.map((f) => ({
      kind: "grade", date: f.created_at,
      titleKey: f.from_name ? "teacherNotificationBell.feedbackFrom" : "teacherNotificationBell.feedbackFromTeacher", titleVars: { name: f.from_name },
      detail: `“${f.note}”${f.rating ? ` · ${f.rating}★` : ""}`, to: "/app/submissions", icon: Star,
    })),
    ...newTeachers.map((t) => ({
      kind: "new_teacher", date: t.member_since || "",
      titleKey: "teacherNotificationBell.joinedNetwork", titleVars: { name: t.name },
      detail: `${(t.categories || []).slice(0, 2).join(", ")}${t.country ? ` · ${t.country}` : ""}`,
      to: `/app/teachers/${t.user_id}`, icon: GraduationCap,
    })),
  ].filter((i) => i.date).sort((a, b) => (b.date > a.date ? 1 : -1)).slice(0, 8));
  return _itemsPromise;
}

function readSeenAt() {
  try {
    return window.localStorage.getItem(SEEN_KEY) || "";
  } catch {
    return "";
  }
}

export default function TeacherNotificationBell() {
  const { tx, t: tr } = useLanguage();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenAt, setSeenAt] = useState(readSeenAt);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchTeacherItems().then((merged) => { if (alive) setItems(merged); });
    return () => { alive = false; };
  }, []);

  // Close on outside click / Escape, so the panel behaves like a normal menu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unread = items.filter((i) => !seenAt || i.date > seenAt).length;

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        // Opening the panel marks everything currently listed as seen.
        const now = new Date().toISOString();
        setSeenAt(now);
        try { window.localStorage.setItem(SEEN_KEY, now); } catch { /* ignore */ }
      }
      return next;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        className="relative p-2 rounded-lg hover:bg-sand-200 transition text-forest-700"
        aria-label={unread ? tr("teacherNotificationBell.unreadTeacherNotifications", { unread }) : tr("teacherNotificationBell.teacherNotifications")}
        data-testid="teacher-notif-bell"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta-500 text-white text-[10px] font-semibold grid place-items-center"
            data-testid="teacher-notif-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 max-w-[min(85vw,calc(var(--app-frame-w)_-_2rem))] rounded-xl border border-sand-300 bg-white shadow-xl z-50 overflow-hidden"
          data-testid="teacher-notif-panel"
        >
          <div className="px-4 py-3 border-b border-sand-200 flex items-center justify-between">
            <span className="font-display font-semibold text-forest-700 text-sm inline-flex items-center gap-2">
              <GraduationCap className="h-4 w-4" /> {tr("teacherNotificationBell.fromYourTeachers")}
            </span>
            <Link
              to="/app/teachers"
              onClick={() => setOpen(false)}
              className="text-xs text-forest-700 hover:underline"
              data-testid="teacher-notif-browse"
            >
              {tr("teacherNotificationBell.browse")}
            </Link>
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              {tr("teacherNotificationBell.noTeacherMessagesYetJoin")}
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-sand-200">
              {items.map((m, i) => (
                <Link
                  key={i}
                  to={m.to}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 flex items-center gap-3 hover:bg-sand-100 transition"
                  data-testid={`teacher-notif-${m.kind}-${i}`}
                >
                  <div className="h-9 w-9 shrink-0 rounded-lg bg-forest-700/10 grid place-items-center text-forest-700">
                    <m.icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-forest-700 truncate">{m.titleKey ? tr(m.titleKey, m.titleVars) : tx(m.title)}</div>
                    <div className="text-xs text-muted-foreground truncate">{m.detailKey ? tr(m.detailKey) : m.detail}</div>
                  </div>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {m.date ? new Date(m.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
